import React from 'react';
import './FullTerms.css';

function FullTerms() {
    return (
        <div className="full-terms-container">
            <div className="full-terms-content">
                <h1>Aceve Open Banking Terms</h1>
                <p className="version-date">Version dated: 07/07/2025</p>
                
                <p>
                    These terms and conditions (the "Terms") govern your use of our open banking services (the "Open Banking Services").
                </p>
                
                <p>
                    The Open Banking Services are made available by Crafty BidCo AB ("Aceve"), reg.no 559462-7381, with registered address at Vendevägen 85 b, 182 91 Danderyd, Sweden. Affiliates of Aceve may be authorised to make available the Open Banking Services to you, and these Terms then govern your use of the Open Banking Services.
                </p>

                <section>
                    <h2>1. Acceptance of the Terms</h2>
                    <p>
                        <strong>1.1</strong> By accessing or using our Open Banking Services, you acknowledge that you have read, understood, and agree to these Terms. If you do not agree with these Terms, do not use the Open Banking Services. These Terms and any additional terms that you agree to, constitute an agreement between you and us.
                    </p>
                    <p>
                        <strong>1.2</strong> You may not use the Open Banking Services for any purpose that is in breach of any agreements with third parties or with applicable law.
                    </p>
                    <p>
                        <strong>1.3</strong> The Open Banking Services are made available only in connection with certain Aceve solutions. The Aceve solutions are governed by a separate agreement or separate terms between you and the relevant affiliate of Aceve that provides such services. These Terms apply in addition to such terms for those services.
                    </p>
                </section>

                <section>
                    <h2>2. The Open Banking Services</h2>
                    <p>
                        <strong>2.1</strong> The Open Banking Services comprise the following services:
                    </p>
                    <ul>
                        <li><strong>Payment Initiation Service</strong> – an online service embedded as a component in the relevant Aceve solution that enables you to initiate a payment order at your request with respect to a payment account held and chosen by you at another payment service provider, usually your online bank or other account holding financial institution.</li>
                        <li><strong>Account Information Service</strong> – an online service embedded as a component in the relevant Aceve solution that enables you to collect from your financial Institutions consolidated information on one or more payment accounts held by you with such financial institution.</li>
                    </ul>
                    <p>
                        <strong>2.2</strong> The Open Banking Services are made available by Aceve in cooperation with Open Payments Europe AB (a Swedish authorised payments institution), being the payment service provider (the "PSP") providing the Payment Initiation Service and the Account Information Service. Additional terms and conditions from the PSP apply to the Payment Initiation Service and the Account Information Service, and these must be accepted before such services can be used, and you will be able to accept these in the relevant part of the onboarding flow for our Open Banking Services.
                    </p>
                    <p>
                        <strong>2.3</strong> In respect of the services provided by the PSP, end-users will be directed to the PSP and the PSP's Payment Initiation Service and Account Information Service will be provided by the PSP directly to the end-users.
                    </p>
                    <p>
                        <strong>2.4</strong> You acknowledge that the PSP may share data and API-content (meaning all data received from each API call to the bank(s)) output from you and any end-users with us and you, as applicable.
                    </p>
                </section>

                <section>
                    <h2>3. Payment Initiation Transactions</h2>
                    <p>
                        <strong>3.1</strong> The PSP acts under its own responsibility to initiate or execute payments in accordance with the PSP's terms and your instructions. We accept no responsibility to initiate or execute payments or for repaying any amounts transferred or paid under the Payment Initiation Service, nor for any additional costs incurred due to delayed or failed payments.
                    </p>
                </section>

                <section>
                    <h2>4. Authorization regarding Account Information</h2>
                    <p>
                        <strong>4.1</strong> By accepting these Terms, you authorize us to access your account information through the Open Banking APIs provided by your bank or payment service provider. This authorization allows us to:
                    </p>
                    <ul>
                        <li>Access your account details</li>
                        <li>Retrieve your transaction history</li>
                        <li>Analyse your financial data to provide our services</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Availability</h2>
                    <p>
                        <strong>5.1</strong> We aim to make available the Open Banking Services with high availability, but we cannot guarantee that the Open Banking Services will always be available. We may, for example due to maintenance reasons, need to restrict the availability or immediately suspend the availability of the Open Banking Services.
                    </p>
                    <p>
                        <strong>5.2</strong> As the Payment Initiation Service and the Account Information Service are provided by the PSP, those services may be unavailable due to reasons attributable to the PSP and we do not assume any responsibility or liability for such unavailability.
                    </p>
                </section>

                <section>
                    <h2>6. Conduct Obligations</h2>
                    <p>
                        <strong>6.1</strong> In order for us to be able to provide the Open Banking Services, you agree that upon our request you will promptly provide us with all information requested by us regarding your, and your end-users', identity and business activities as instructed by us from time to time. We are authorized to provide all such information to the PSP.
                    </p>
                    <p>
                        <strong>6.2</strong> To the extent you do not comply with the PSP's acceptable use policy, we may suspend and discontinue the Open Banking Services, and the PSP may suspend and discontinue the Payment Initiation Service and the Account Information Service.
                    </p>
                </section>
                
                <section>
                    <h2>7. Term and Termination</h2>
                    <p>
                        <strong>7.1</strong> The Open Banking Services are, unless otherwise agreed, available for the duration of the agreed term for the relevant Aceve solution, subject to you complying with the pricing and payment terms.
                    </p>
                </section>

                <section>
                    <h2>8. Pricing and Payment</h2>
                    <p>
                        <strong>8.1</strong> The pricing and payment terms that apply to the Open Banking Services are available upon request.
                    </p>
                </section>

                <section>
                    <h2>9. Liability</h2>
                    <p>
                        <strong>9.1</strong> The Open Banking Services are made available to you on an "as is" and "as available" basis. We shall in no case be liable to you for any direct or indirect loss or damages incurred due to the use of the Open Banking Services, in particular any:
                    </p>
                    <ul>
                        <li>financial loss, delay or failure to perform our obligations under these Terms by reason of any cause beyond our reasonable control, including but not limited to any action or inaction by you or any third party, any force majeure event, bank delay, technical failures including IT failures of either us or third party service providers used by us (including the PSP), failure or delay of any electronic transmission, any accident, emergency, act of god, pandemics or any abnormal or unforeseeable circumstances; or</li>
                        <li>consequential or indirect loss (such as loss of profits or opportunity) you may incur as a result of us failing to perform our duties under these Terms.</li>
                    </ul>
                    <p>
                        <strong>9.2</strong> You are responsible for all liabilities, financial or otherwise, incurred by us or a third party caused by or arising out of your breach of these Terms and your use of the Open Banking Services. You agree to reimburse us, or such third party for all such liability.
                    </p>
                    <p>
                        <strong>9.3</strong> We are not liable, and you agree not to hold us responsible, for any damages or losses resulting directly or indirectly from (i) your inability to use the Open Banking Services for whatever reason; (ii) delays or disruptions in the Open Banking Services; (iii) glitches, bugs, errors, or inaccuracies of any kind in the Open Banking Services; or (iv) a suspension or other action taken with respect to the Open Banking Services.
                    </p>
                    <p>
                        <strong>9.4</strong> Nothing in these Terms shall operate to exclude liability for fraud or gross negligence.
                    </p>
                </section>

                <section>
                    <h2>10. Changes to the Services and these Terms</h2>
                    <p>
                        <strong>10.1</strong> We reserve the right to update and change the Open Banking Services and these Terms from time to time. Your continued use of the Open Banking Service after an update or change to the Open Banking Services or these Terms will be deemed as your acceptance of such update or change. Any updated version of these Terms will be posted on our web page.
                    </p>
                </section>

                <section>
                    <h2>11. Governing Law</h2>
                    <p>
                        <strong>11.1</strong> These Terms shall be governed by the substantive laws of Sweden.
                    </p>
                </section>

                <section>
                    <h2>12. Dispute Resolution</h2>
                    <p>
                        <strong>12.1</strong> The dispute resolution clause in your existing agreement (or in the existing terms) for the relevant Aceve solution shall apply if there is a dispute between you and us (meaning the relevant Aceve entity providing the relevant Aceve solution) regarding the Open Banking Services or these Terms.
                    </p>
                </section>
            </div>
        </div>
    );
}

export default FullTerms; 